// src/utils/repeatableData.js
// ─────────────────────────────────────────────────────────────────────────────
// FINDING A REPEATABLE SECTION'S SAVED ROWS.
//
// Repeatable entries are persisted under the section's DISPLAY NAME:
//
//   "6. Name and contact details of the foreign repository from where the
//    biological resource are to be deposited": [ { … } ]
//
// A display name is not an identifier. It carries a serial the authority
// renumbers, and prose the authority rewords. The moment Form E's sections were
// renumbered — the foreign repository moving from 6 to 5, the Indian repository
// from 7 to 6 — every application already submitted became unreadable: the
// template asked for "5. Name and contact details of the foreign repository…",
// the data sat under "6. …", the lookup missed, and the preview said
// "No entries recorded." over rows that were right there in the payload.
//
// A second way the same data goes missing: a section that USED to collect one
// set of answers inline and is now a repeatable. Those applications hold the
// values flat inside their page object — `microorganism_scientific_name` and
// friends sitting in "Part 2" — with no array anywhere to find.
//
// So resolution is a stated strategy rather than a lookup, tried in order of
// how much it can be trusted, and never guessing twice about the same rows:
//
//   1. the exact key                     — what a current application saves
//   2. the same name, serial ignored     — survives renumbering and rewording
//   3. the entry's own field names       — survives the name changing entirely
//   4. one row rebuilt from flat values  — survives a section becoming repeatable
//
// Steps 1–2 run for EVERY repeatable before step 3 runs for any, so a confident
// match is never beaten to its rows by another section's guess. Each saved array
// is claimed once. A repeatable that matches nothing gets [] — an empty section,
// which is the honest answer, not another section's entries.
// ─────────────────────────────────────────────────────────────────────────────

const META_PREFIX = '_';

/** "6. Name and contact details of the foreign repository…" → comparable form. */
export const canonicalSectionKey = name => String(name ?? '')
  .replace(/^\s*\(?\s*[0-9]+|^\s*\(?\s*[ivxlcdm]+(?=\s*[).])/i, '')  // leading serial
  .replace(/^[\s).:-]+/, '')
  .replace(/[^a-z0-9]+/gi, ' ')
  .trim()
  .toLowerCase();

/**
 * Every field name a repeatable's rows can carry — ALL conditional branches, not
 * just an active one, because this identifies the SHAPE of an entry rather than
 * the content of any particular row. Nested repeatables are their own sections.
 */
export function repeatableLeafNames(fieldDefs) {
  const out = new Set();
  const walk = defs => (defs || []).forEach(f => {
    if (!f || f.type === 'repeatable') return;
    if (f.sectionType === 'subSection') { walk(f.fields); return; }
    if (f.type === 'conditionalGroup') {
      Object.values(f.conditions || {}).forEach(br => { if (Array.isArray(br)) walk(br); });
      return;
    }
    if (f.name) out.add(f.name);
    if (f.othersConditionalField?.name) out.add(f.othersConditionalField.name);
  });
  walk(fieldDefs);
  return out;
}

/** Collect every repeatable the template declares, in page order. */
export function collectRepeatableDefs(pages = []) {
  const out = [];
  const seen = new Set();
  const walk = defs => (defs || []).forEach(f => {
    if (!f) return;
    if (f.type === 'repeatable') {
      const key = f.sectionName || 'default';
      if (!seen.has(key)) { seen.add(key); out.push({ key, fieldDefs: f.fields || [] }); }
      walk(f.fields);                      // a repeatable may nest another
      return;
    }
    if (f.sectionType === 'subSection') { walk(f.fields); return; }
    if (f.type === 'conditionalGroup') {
      Object.values(f.conditions || {}).forEach(br => { if (Array.isArray(br)) walk(br); });
    }
  });
  (pages || []).forEach(pg => (pg?.sections || []).forEach(sec => walk(sec?.fields)));
  return out;
}

/* Saved arrays that could be somebody's rows: a non-empty array of objects,
   under a key that is not metadata. */
const savedRowArrays = bag => Object.entries(bag || {})
  .filter(([k, v]) => !k.startsWith(META_PREFIX)
    && !k.endsWith('_bulkUpload')
    && Array.isArray(v) && v.length > 0
    && v.every(r => r && typeof r === 'object' && !Array.isArray(r)))
  .map(([k, v]) => ({ key: k, rows: v }));

/* How well a saved array's rows match a repeatable's field shape: the share of
   the entry's own field names that actually appear in the saved rows. */
const signatureScore = (rows, leafNames) => {
  if (!leafNames.size) return 0;
  const present = new Set();
  rows.forEach(r => Object.keys(r || {}).forEach(k => { if (leafNames.has(k)) present.add(k); }));
  return present.size / leafNames.size;
};
/* Below this the overlap is coincidence (two sections both collecting "city"),
   not identity. */
const SIGNATURE_MIN = 0.5;

/* Companion keys a value carries with it — the unit of a quantity, the proof
   type and ID number of an upload. Rebuilding a row without them would lose
   what those values MEAN. */
const companionKeys = name => [`${name}_unit`, `${name}_proofType`, `${name}_proof_type`, `${name}_idNumber`, `${name}_id_number`];

const isBlank = v => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/**
 * Rebuild ONE entry from values saved flat — the shape left behind by a section
 * that used to collect a single set of answers inline and is now a repeatable.
 * Returns [] unless the flat data actually carries this entry's own fields.
 */
function rebuildFromFlat(leafNames, sources) {
  const row = {};
  let filled = 0;
  leafNames.forEach(name => {
    for (const src of sources) {
      if (!src) continue;
      if (!isBlank(src[name])) { row[name] = src[name]; filled++; break; }
    }
    companionKeys(name).forEach(ck => {
      for (const src of sources) {
        if (src && !isBlank(src[ck]) && row[ck] === undefined) { row[ck] = src[ck]; break; }
      }
    });
  });
  return filled > 0 ? [row] : [];
}

/**
 * Resolve every repeatable's rows in one pass.
 *
 * @param {Array}  repeatables  [{ key, fieldDefs }] from collectRepeatableDefs
 * @param {object} bag          the saved data (arrays, section objects, scalars)
 * @param {object} opts
 * @param {Array}  opts.flatSources  extra objects to rebuild a flat entry from,
 *                                   most specific first (the section, then root)
 * @returns {Map<string, Array>} template key → rows (possibly empty)
 */
export function resolveRepeatableRows(repeatables = [], bag = {}, { flatSources = [] } = {}) {
  const resolved = new Map();
  const available = savedRowArrays(bag);
  const claimed = new Set();
  const take = (key, entry) => { claimed.add(entry.key); resolved.set(key, entry.rows); };

  // 1 & 2 — by name: exact first, then with the serial and punctuation ignored.
  const pending = [];
  repeatables.forEach(rep => {
    const exact = available.find(a => !claimed.has(a.key) && a.key === rep.key);
    if (exact) { take(rep.key, exact); return; }
    pending.push(rep);
  });
  const stillPending = [];
  pending.forEach(rep => {
    const want = canonicalSectionKey(rep.key);
    const byName = want && available.find(a => !claimed.has(a.key) && canonicalSectionKey(a.key) === want);
    if (byName) { take(rep.key, byName); return; }
    stillPending.push(rep);
  });

  /* 3 — by shape. Every remaining (repeatable × saved array) pairing is scored
     and the strongest is settled first, so the best-matching section claims its
     own rows rather than whichever section happened to be walked first. */
  const scored = [];
  stillPending.forEach(rep => {
    const leaves = repeatableLeafNames(rep.fieldDefs);
    available.forEach(a => {
      if (claimed.has(a.key)) return;
      const score = signatureScore(a.rows, leaves);
      if (score >= SIGNATURE_MIN) scored.push({ rep, a, score });
    });
  });
  scored.sort((x, y) => y.score - x.score);
  scored.forEach(({ rep, a }) => {
    if (resolved.has(rep.key) || claimed.has(a.key)) return;
    take(rep.key, a);
  });

  // 4 — nothing saved as rows: rebuild a single entry from flat values.
  repeatables.forEach(rep => {
    if (resolved.has(rep.key)) return;
    resolved.set(rep.key, rebuildFromFlat(repeatableLeafNames(rep.fieldDefs), [bag, ...flatSources]));
  });

  return resolved;
}

/**
 * The saved bag with every repeatable's rows moved onto the key the CURRENT
 * template uses — what the form screen needs before it can prefill an entry
 * table. Keys that were resolved under an old name are dropped so a re-save does
 * not persist both spellings.
 */
export function remapRepeatableKeys(repeatables = [], bag = {}, opts = {}) {
  const resolved = resolveRepeatableRows(repeatables, bag, opts);
  const out = { ...bag };
  const templateKeys = new Set(repeatables.map(r => r.key));
  const savedKeys = new Set(savedRowArrays(bag).map(a => a.key));

  resolved.forEach((rows, key) => {
    if (!rows.length && !savedKeys.has(key)) return;   // nothing to write
    out[key] = rows;
  });
  // Drop the stale spellings we just re-homed, but never a key the template
  // still uses and never anything we could not account for.
  savedKeys.forEach(k => {
    if (templateKeys.has(k)) return;
    const rehomed = [...resolved.values()].some(rows => rows === bag[k]);
    if (rehomed) delete out[k];
  });
  return out;
}

export default resolveRepeatableRows;
